/**
 * Media Grid Component
 *
 * Displays media assets in a responsive grid layout.
 */

import { useRef } from 'react';
import { ZprEmptyState } from '@zpr-stencil';
import { ProcessingIcon } from '../../../icons';
import type { IAsset } from '../providers';
import { AssetCard } from './AssetCard';
import { AssetTable } from './AssetTable';
import { EmptyStateSection } from './EmptyStateSection';


export interface MediaGridProps {
  /** Array of media assets to display */
  assets: IAsset[];
  /** Currently selected asset IDs */
  selectedAssets: string[];
  /** Callback when an asset is clicked */
  onAssetClick?: (asset: IAsset) => void;
  /** Callback when an asset is selected/deselected */
  onAssetSelect?: (assetId: string) => void;
  /** Whether the grid is in loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string;
  /** Number of columns for the grid layout */
  columns?: 2 | 3 | 4 | 5 | 6 | 'auto';
  /** Whether the grid is in table view */
  isTableView?: boolean;
  /** Enable multi-selection of assets for passing to AssetTable */
  enableMultiSelect?: boolean;
  /** IDs visually highlighted by an in-progress drag selection (managed by parent) */
  dragHighlightedIds?: string[];
  /** Whether a drag is currently in progress (suppresses card clicks) */
  isDragActive?: boolean;
  /** Custom CSS class */
  className?: string;
}

export const MediaGrid = ({
  assets,
  selectedAssets,
  onAssetClick,
  onAssetSelect,
  loading = false,
  error,
  columns = 'auto',
  isTableView = false,
  enableMultiSelect,
  dragHighlightedIds = [],
  isDragActive = false,
  className = '',
}: MediaGridProps) => {

  // Guard: suppress card clicks that fire right after a drag ends
  const dragJustEndedRef = useRef(false);

  const handleAssetClick = (asset: IAsset) => {
    // Suppress click events that are the tail-end of a drag selection
    if (dragJustEndedRef.current) return;

    // When a card is clicked, we handle selection
    if (onAssetSelect) {
      onAssetSelect(asset.getId());
    }
    // Also trigger the click callback if provided
    if (onAssetClick) {
      onAssetClick(asset);
    }
  };

  // Track isDragActive transitions to set the click guard
  const prevDragActive = useRef(isDragActive);
  if (prevDragActive.current && !isDragActive) {
    // Drag just ended — suppress the next click
    dragJustEndedRef.current = true;
    requestAnimationFrame(() => {
      dragJustEndedRef.current = false;
    });
  }
  prevDragActive.current = isDragActive;

  if (loading) {
    return (
      <div className={`media-grid ${className}`}>
        <div className="loading-spinner">
          <ProcessingIcon />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`media-grid ${className}`}>
        <ZprEmptyState>
          <svg slot="icon" width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span slot="title">Error loading assets</span>
          <span slot="description">{error}</span>
        </ZprEmptyState>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className={`media-grid media-grid--empty ${className}`}>
        <EmptyStateSection sectionTitle="Media Library" />
      </div>
    );
  }

  return (
    isTableView ? (
      <AssetTable
        assets={assets}
        selectedAssets={selectedAssets}
        onAssetClick={onAssetClick}
        onAssetSelect={onAssetSelect}
        enableMultiSelect={enableMultiSelect}
        dragHighlightedIds={dragHighlightedIds}
        isDragActive={isDragActive}
        className={className}
      />
    ) : (
      <div className={`media-grid media-grid--${columns} ${className}`}>
        {assets.map((asset) => (
          <AssetCard
            key={asset.getId()}
            asset={asset}
            enableMultiSelect={enableMultiSelect}
            isSelected={selectedAssets.includes(asset.getId())}
            isDragHighlighted={dragHighlightedIds.includes(asset.getId())}
            showStatusPill={true}
            showTitle={true}
            onClick={() => handleAssetClick(asset)}
          />
        ))}
      </div>
    )
  );
};